import { call, put, select } from "redux-saga/effects";
import Api from "../Api";

// CREW-1209 — names for a filter restored from a URL.
//
// List filters serialise as ids only (the CREW-1169 contract), so a link from a
// colleague, a reload or back/forward gives us option values with no labels and
// the chips read "2 selected". CREW-1208 covers the warm case — applying a
// filter carries the labels across from the store — but on a cold entry the
// application has never seen them.
//
// They cannot be recovered from the option lists either: those load the first
// 30 records (`SelectOption::Base` — `count || 30`), so an owner further down
// the alphabet would stay unnamed however long you wait. Only a request by id
// answers, and that is what `POST /select_options/resolve` is for.
//
// Shared by page, because every list that adopts the URL contract needs it.
// The page supplies the map of its own filter keys to backend types — only the
// page knows its keys, and not every key needs resolving (ranks are already in
// the store).

const isOptionList = (value) => Array.isArray(value);

/**
 * collectUnresolved
 * Which ids the backend has to name, grouped by type. Only option values
 * without a `label`, and only keys the page declared resolvable.
 * @param {Object} filter
 * @param {Object} types - {filterKey: backendType}
 * @returns {Array<{type: String, ids: Array<String>}>}
 */
export const collectUnresolved = (filter = {}, types = {}) =>
  Object.entries(types).reduce((batch, [key, type]) => {
    const value = (filter || {})[key];
    if (!isOptionList(value)) return batch;

    const ids = [
      ...new Set(
        value
          .filter((option) => option && option.label === undefined && option.value !== undefined)
          .map((option) => `${option.value}`),
      ),
    ];
    if (ids.length) batch.push({ type, ids });

    return batch;
  }, []);

/**
 * withResolvedLabels
 * Apply the endpoint's answer to the filter. Values the response did not
 * mention keep no label — a link can outlive the record it points at, and the
 * page must still open.
 *
 * A label the filter already carries is never overwritten: the response may
 * arrive after the user has picked something, and what they see wins.
 * @param {Object} filter
 * @param {Object} types - {filterKey: backendType}
 * @param {Object} resolved - {backendType: [{value, label}]}
 * @returns {Object} filter
 */
export const withResolvedLabels = (filter = {}, types = {}, resolved = {}) =>
  Object.entries(types).reduce((acc, [key, type]) => {
    const value = acc[key];
    const options = (resolved || {})[type];
    if (!isOptionList(value) || !isOptionList(options) || !options.length) return acc;

    const labels = new Map(options.map((option) => [`${option.value}`, option.label]));

    return {
      ...acc,
      [key]: value.map((option) => {
        if (!option || option.label !== undefined) return option;
        const label = labels.get(`${option.value}`);
        return label === undefined ? option : { ...option, label };
      }),
    };
  }, { ...filter });

/**
 * createResolveFilterLabels
 * The saga: read the filter, ask for the missing names in ONE request, hand the
 * answer to the reducer. Nothing happens when there is nothing to name, which
 * is the common case — applying a filter carries its own labels.
 *
 * A failure is logged and dropped: unnamed chips are a cosmetic loss, and the
 * list itself is already loading from the same filter.
 * @param {Object} params
 * @param {Function} params.selectFeature - state → the feature slice
 * @param {Object} params.types - {filterKey: backendType}
 * @param {Object} params.actions - needs `setFilterLabels`
 * @returns {Function} saga
 */
export const createResolveFilterLabels = ({ selectFeature, types, actions }) =>
  function* resolveFilterLabels() {
    const { filter } = yield select(selectFeature);
    const batch = collectUnresolved(filter, types);
    if (!batch.length) return;

    try {
      const resolved = yield call(Api.selectOptions.resolve, batch);
      yield put(actions.setFilterLabels(resolved));
    } catch (e) {
      console.error(e);
    }
  };

export default createResolveFilterLabels;
